import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import Button from '../../components/Button';
import { scrollToElement } from '../../utils/helpers';
import './Hero.css';

const heroBackgrounds = ['/hero/1.webp', '/hero/2.webp', '/hero/3.webp', '/hero/4.webp', '/hero/5.webp'];

const Hero = () => {
    const { t } = useTranslation();
    const [activeSlide, setActiveSlide] = useState(0);
    // Пять фонов — это ~300 КБ. Первый нужен сразу (это LCP), остальные подгружаем,
    // когда страница уже отрисована: до второго слайда всё равно 5 секунд.
    const [backgroundsReady, setBackgroundsReady] = useState(false);

    useEffect(() => {
        const load = () => setBackgroundsReady(true);
        if (document.readyState === 'complete') {
            const id = window.setTimeout(load, 300);
            return () => window.clearTimeout(id);
        }
        window.addEventListener('load', load, { once: true });
        return () => window.removeEventListener('load', load);
    }, []);

    useEffect(() => {
        const interval = window.setInterval(() => {
            setActiveSlide((prev) => (prev + 1) % heroBackgrounds.length);
        }, 5000);

        return () => window.clearInterval(interval);
    }, []);

    const handleBookClick = () => {
        scrollToElement('contact-form');
    };

    const handleLearnClick = () => {
        scrollToElement('insoles');
    };

    const handlePrevSlide = () => {
        setActiveSlide((prev) => (prev - 1 + heroBackgrounds.length) % heroBackgrounds.length);
    };

    const handleNextSlide = () => {
        setActiveSlide((prev) => (prev + 1) % heroBackgrounds.length);
    };

    return (
        <section id="hero" className="hero">
            {/* Background Decoration */}
            <div className="hero-bg">
                {heroBackgrounds.map((image, index) => (
                    <div
                        key={image}
                        className={`hero-bg-slide hero-bg-slide-${index} ${index === activeSlide ? 'is-active' : ''}`}
                        style={
                            index === 0 || backgroundsReady || index === activeSlide
                                ? { backgroundImage: `url(${image})` }
                                : undefined
                        }
                        aria-hidden="true"
                    ></div>
                ))}
                <div className="hero-blob hero-blob-1"></div>
                <div className="hero-blob hero-blob-2"></div>
                <div className="hero-gradient"></div>
            </div>

            <div className="container">
                <div className="hero-content">
                    <motion.div
                        className="hero-text"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <motion.span
                            className="hero-badge"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.4, delay: 0.2 }}
                        >
                            <img
                                src="/food.png"
                                alt="Podiatech Logo"
                                className="hero-badge-logo"
                                width="32"
                                height="32"
                                decoding="async"
                                fetchpriority="high"
                            />
                            <span>Podiatech Uzbekistan</span>
                        </motion.span>

                        <h1 className="hero-title hero-text-glass">
                            {t('hero.title')}
                            <span className="hero-title-highlight">{t('hero.title_highlight')}</span>
                        </h1>

                        <p className="hero-subtitle hero-text-glass">
                            {t('hero.subtitle')}
                        </p>

                        <motion.div
                            className="hero-actions"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                        >
                            <Button
                                variant="primary"
                                size="lg"
                                onClick={handleBookClick}
                            >
                                {t('hero.cta_book')}
                            </Button>
                            <Button
                                variant="secondary"
                                size="lg"
                                onClick={handleLearnClick}
                            >
                                {t('hero.cta_learn')}
                            </Button>
                        </motion.div>

                        {/* Trust Indicators */}
                        <motion.div
                            className="hero-trust"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.5 }}
                        >
                            <div className="trust-item trust-glass">
                                <span className="trust-icon" aria-hidden="true">
                                    <FontAwesomeIcon icon={faCheck} />
                                </span>
                                <span>{t('hero.mini_detailes')}</span>
                            </div>
                            <div className="trust-item trust-glass">
                                <span className="trust-icon" aria-hidden="true">
                                    <FontAwesomeIcon icon={faCheck} />
                                </span>
                                <span>{t('hero.mini_detailes2')}</span>
                            </div>
                            <div className="trust-item trust-glass">
                                <span className="trust-icon" aria-hidden="true">
                                    <FontAwesomeIcon icon={faCheck} />
                                </span>
                                <span>{t('hero.mini_detailes3')}</span>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>

            <div className="hero-slider-controls">
                <button
                    type="button"
                    className="hero-nav hero-nav-prev"
                    onClick={handlePrevSlide}
                    aria-label="Previous background"
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <div className="hero-pagination">
                    {heroBackgrounds.map((_, index) => (
                        <button
                            key={`dot-${index}`}
                            type="button"
                            className={`hero-dot ${index === activeSlide ? 'is-active' : ''}`}
                            onClick={() => setActiveSlide(index)}
                            aria-label={`Go to background ${index + 1}`}
                        ></button>
                    ))}
                </div>
                <button
                    type="button"
                    className="hero-nav hero-nav-next"
                    onClick={handleNextSlide}
                    aria-label="Next background"
                >
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            </div>

        </section>
    );
};

export default Hero;
